import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useGarageStore } from '@/stores/garage.js'

export const useRequestAQuoteStore = defineStore('request_a_quote', () => {

  // I18n
  const { t } = useI18n()

  // Stores
  const router = useRouter()
  const route = useRoute()
  const garageStore = useGarageStore()
  const authenticationStore = useAuthenticationStore()

  // Variables
  const currentStep = ref(1)
  const maxReachedStep = ref(1)
  
  const selectedServices = ref([
    {
      category: t('request_a_quote.service_section.service_category_placeholder'),
      optionChoices: [{
        category_option: t('request_a_quote.service_section.option_placeholder'),
        category_option_choice: t('request_a_quote.service_section.choice_placeholder')
      }]
    }
  ])
  const blankService = ref({
    category: t('request_a_quote.service_section.service_category_placeholder'),
    optionChoices: [{
      category_option: t('request_a_quote.service_section.option_placeholder'),
      category_option_choice: t('request_a_quote.service_section.choice_placeholder')
    }]
  })
  const blankOptionSet = ref({
    category_option: t('request_a_quote.service_section.option_placeholder'),
    category_option_choice: t('request_a_quote.service_section.choice_placeholder')
  })

  const defaultSelectedLocation = {
    location_name: t('request_a_quote.location_section.location_dropdown.select_location_placeholder'),
    user_address_id: null,
    address: null,
    address_2: null,
    city: null,
    state: null,
    country: null,
    latitude: 0,
    longitude: 0,
    isValid: false
  }
  const selectedLocation = ref(JSON.parse(JSON.stringify(defaultSelectedLocation)))

  const selectedMileRadius = ref(5)

  const requestedServiceProviderId = ref(null)

  // Functions
  function addService() {
    selectedServices.value.push(JSON.parse(JSON.stringify(blankService.value)))
  }

  function addOptionSet(index) {
    selectedServices.value[index].optionChoices.push(JSON.parse(JSON.stringify(blankOptionSet.value)))
  }

  function setLocation(location) {
    if (location) {
      selectedLocation.value = {
        ...JSON.parse(JSON.stringify(defaultSelectedLocation)),
        ...location,
        isValid: locationIsValid(location)
      }
    }
  }

  function locationIsValid(location) {
    return !!(location.address
      && location.city
      && location.state
      && location.country
      && location.latitude !== 0
      && location.longitude !== 0)
  }

  // Submit Quote
  const submitQuoteRequestData = computed(() => {
    return {
      servicechek_user_quote: {
        user_vehicle_id: garageStore.currentVehicle?.id,
        services: selectedServices.value,
        user_address: selectedLocation.value,
        radius: selectedMileRadius.value,
        requested_service_provider_id: requestedServiceProviderId.value,
      }
    }
  })

  const {
    execute: submitQuoteExecute,
    onFetchResponse,
    data: submitQuoteData
  } = useIntelichekFetch(`${import.meta.env.VITE_BACKEND_URL}/api/v2/servicechek/user_quotes`, { immediate: false })
    .post(submitQuoteRequestData).json()

  onFetchResponse(async (response) => {
    const quote = await response.json()
    
    // Show success toast
    try {
      const { useToastStore } = await import('@/stores/toast.js');
      const toastStore = useToastStore();
      toastStore.success(t('toast.quote_created'));
    } catch (e) {
      console.log('Toast notification failed:', e);
    }
    
    router.push({ name: 'RequestAQuote', params: { step: Number(route.params.step) + 1}, query: {quoteId: quote.id } })
  })

  function submitQuote() {
    if(canSubmitQuote) {
      submitQuoteExecute();

      // Temporarily update the counter while we wait for the backend to process the request
      // and actually deduct from the counter on the users table
      authenticationStore.currentUser.user.attributes.additional_quota -= 1;

      // This is a pretty ugly bodge just in case there's some delay on backend processing
      // Realistically we should be using a websocket to update the real time quote counter
      // But we are down to the wire...
      setTimeout(() => authenticationStore.updateUserData(), 2000);
    }
  }

  const canSubmitQuoteSelectedServiceValid = computed(() => {
    if (selectedServices.value && selectedServices.value[0]) {
      return !!(selectedServices.value[0].category && selectedServices.value[0].category.id)
    }
    return false
  })

  const canSubmitQuote = computed(() => {
    const totalQuota = authenticationStore.currentUser.user.attributes.additional_quota || 0 +
      authenticationStore.currentUser.user.attributes.monthly_quota || 0 +
      authenticationStore.currentUser.user.attributes.yearly_quota || 0;

    if (totalQuota <= 0) return false
    
    const allStepsValid = [isStep1Valid.value, isStep2Valid.value, isStep3Valid.value]
    return allStepsValid.every(Boolean)
  })

  // Stepper Functions
  function setCurrentStep(step) {
    currentStep.value = step
    if (step > maxReachedStep.value) {
      maxReachedStep.value = step
    }
  }
  
  function getStepStatus(stepNumber, currentStepNumber) {
    if (stepNumber === currentStepNumber) {
      // Show checkmark for current step if it's valid
      if (stepNumber === 3 && isStep3Valid.value) {
        return -1
      }
      return 0 // current
    }
    if (stepNumber < maxReachedStep.value) {
      return -1 // completed
    }
    return 1 // future
  }
  
  // Computed step validations
  const isStep1Valid = computed(() => !!garageStore.currentVehicle?.id)
  const isStep2Valid = computed(() => canSubmitQuoteSelectedServiceValid.value)
  const isStep3Valid = computed(() => {
    const loc = selectedLocation.value
    return !!(loc && loc.latitude !== 0 && loc.longitude !== 0)
  })
  const isStep4Valid = computed(() => maxReachedStep.value >= 4)
  
  function $reset() {
    currentStep.value = 1
    maxReachedStep.value = 1
    selectedServices.value = [JSON.parse(JSON.stringify(blankService.value))]
    selectedLocation.value = JSON.parse(JSON.stringify(defaultSelectedLocation))
    selectedMileRadius.value = 5
    submitQuoteData.value = null
    requestedServiceProviderId.value = null
  }

  function clearPersistedState() {
    sessionStorage.removeItem('request_a_quote')
    $reset()
  }

  function setServiceProvider(serviceProviderId) {
    requestedServiceProviderId.value = serviceProviderId;
  }

  return {
    // Stepper state
    currentStep,
    maxReachedStep,
    setCurrentStep,
    getStepStatus,
    isStep1Valid,
    isStep2Valid,
    isStep3Valid,
    isStep4Valid,
    // Existing state
    selectedServices,
    selectedLocation,
    selectedMileRadius,
    requestedServiceProviderId,
    addService,
    addOptionSet,
    setLocation,
    submitQuote,
    submitQuoteData,
    canSubmitQuote,
    canSubmitQuoteSelectedServiceValid,
    $reset,
    clearPersistedState,
    setServiceProvider
  }
}, {
  persist: {
    storage: sessionStorage,
  }
})